import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  LoadingState,
  StatsCard,
  type Column,
} from "@agentnative/ui";
import { useCurrentOrganizationId } from "../stores/currentOrg";
import { apiErrorMessage } from "../lib/api/client";
import {
  SUBSCRIPTION_STATUS_LABELS,
  getCreditBalance,
  getSubscription,
  listTransactions,
  subscriptionBadgeVariant,
} from "../lib/api/billing";
import type { CreditTransaction } from "../lib/api/types";

export function BillingRoute() {
  const organizationId = useCurrentOrganizationId();

  const subscriptionQuery = useQuery({
    queryKey: ["subscription", organizationId],
    queryFn: () => getSubscription(organizationId),
    enabled: !!organizationId,
  });

  const balanceQuery = useQuery({
    queryKey: ["credit-balance", organizationId],
    queryFn: () => getCreditBalance(organizationId),
    enabled: !!organizationId,
  });

  const transactionsQuery = useQuery({
    queryKey: ["transactions", organizationId],
    queryFn: () => listTransactions(organizationId, { limit: 50, offset: 0 }),
    enabled: !!organizationId,
  });

  if (!organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Set an organization_id in the dev auth panel above to view billing."
      />
    );
  }

  const columns: Column<CreditTransaction>[] = [
    {
      key: "createdAt",
      label: "Date",
      render: (t) => new Date(t.createdAt).toLocaleString(),
    },
    {
      key: "direction",
      label: "Direction",
      render: (t) => (
        <Badge variant={t.direction === "CREDIT_DIRECTION_CREDIT" ? "success" : "default"}>
          {t.direction === "CREDIT_DIRECTION_CREDIT" ? "Credit" : "Debit"}
        </Badge>
      ),
    },
    { key: "amount", label: "Amount", render: (t) => t.amount },
    { key: "balanceAfter", label: "Balance After", render: (t) => t.balanceAfter },
    { key: "reason", label: "Reason" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Billing</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-2 text-sm font-medium text-[var(--color-neutral-600)]">
              Subscription
            </h2>
            {subscriptionQuery.isLoading ? (
              <LoadingState size="sm" />
            ) : subscriptionQuery.isError ? (
              <Alert variant="danger">{apiErrorMessage(subscriptionQuery.error)}</Alert>
            ) : (
              <div className="space-y-1">
                <Badge variant={subscriptionBadgeVariant(subscriptionQuery.data!.subscription.status)}>
                  {SUBSCRIPTION_STATUS_LABELS[subscriptionQuery.data!.subscription.status]}
                </Badge>
                <p className="text-sm text-[var(--color-neutral-600)]">
                  Tier: {subscriptionQuery.data!.subscription.tier || "-"}
                </p>
                {subscriptionQuery.data!.subscription.cancelAtPeriodEnd && (
                  <p className="text-xs text-[var(--color-neutral-500)]">
                    Cancels at period end.
                  </p>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {balanceQuery.isLoading ? (
          <Card padding="lg">
            <CardBody>
              <LoadingState size="sm" />
            </CardBody>
          </Card>
        ) : balanceQuery.isError ? (
          <Card padding="lg">
            <CardBody>
              <Alert variant="danger">{apiErrorMessage(balanceQuery.error)}</Alert>
            </CardBody>
          </Card>
        ) : (
          <StatsCard label="Credit Balance" value={balanceQuery.data!.balance.balance} />
        )}

        {balanceQuery.data && (
          <StatsCard label="Reserved Credits" value={balanceQuery.data.balance.reserved} />
        )}
      </div>

      <div>
        <h2 className="mb-3 text-lg font-medium">Transaction History</h2>
        {transactionsQuery.isLoading ? (
          <LoadingState message="Loading transactions..." />
        ) : transactionsQuery.isError ? (
          <Alert variant="danger" title="Failed to load transactions">
            {apiErrorMessage(transactionsQuery.error)}
          </Alert>
        ) : (
          <Card padding="none">
            <DataTable<CreditTransaction>
              data={transactionsQuery.data?.transactions ?? []}
              columns={columns}
              emptyMessage="No transactions yet."
            />
          </Card>
        )}
      </div>
    </div>
  );
}
